import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PaymentService from 'services/PaymentService';

const DEFAULT_PLAN = {
	phonebookLimit: 0,
	deviceLimit: 0,
	agentAccess: true,
	dialer: true,
	callBroadcast: true,
	agentIa: true
};

/**
 * Normalise le plan depuis GET /subscription/current (camelCase ou snake_case).
 */
export function normalizePlanFromSubscription(root) {
	const plan = root?.plan;
	if (!plan) {
		return { ...DEFAULT_PLAN };
	}
	const pb = Number(plan.phonebook_limit ?? plan.phonebookLimit ?? 0);
	const dev = Number(plan.device_limit ?? plan.deviceLimit ?? 0);
	return {
		phonebookLimit: Number.isFinite(pb) && pb > 0 ? pb : 0,
		deviceLimit: Number.isFinite(dev) && dev > 0 ? dev : 0,
		agentAccess: plan.agent_access !== false && plan.agentAccess !== false,
		dialer: plan.dialer !== false,
		callBroadcast: plan.call_broadcast !== false && plan.callBroadcast !== false,
		agentIa:
			plan.agent_ia !== false &&
			plan.agentIa !== false &&
			plan.messaging !== false
	};
}

/** Rôles pour lesquels on charge le plan tenant — utilisé pour éviter d’afficher le menu « plan » avant la réponse API. */
export function shouldFetchPlanForRole(role) {
	const r = (role || '').toLowerCase();
	return r === 'manager' || r === 'staff' || r === 'agent';
}

/**
 * Droits / limites du plan du tenant (abonnement du manager lié à l’utilisateur).
 * Pour un agent, GET /subscription/current renvoie l’abonnement de son manager (managerUuid sur le compte agent).
 */
export default function useManagerPlanEntitlements() {
	const { role } = useSelector((state) => state.auth);
	const [plan, setPlan] = useState(() => ({ ...DEFAULT_PLAN }));
	const [loading, setLoading] = useState(true);

	const refresh = useCallback(async () => {
		if (!shouldFetchPlanForRole(role)) {
			setPlan({ ...DEFAULT_PLAN });
			setLoading(false);
			return;
		}
		setLoading(true);
		try {
			const res = await PaymentService.getCurrentSubscription();
			const root = res?.data ?? res;
			setPlan(normalizePlanFromSubscription(root));
		} catch {
			setPlan({ ...DEFAULT_PLAN });
		} finally {
			setLoading(false);
		}
	}, [role]);

	useEffect(() => {
		void refresh();
	}, [refresh]);

	return {
		plan,
		loading,
		refresh,
		isDialerAllowed: plan.dialer,
		isCallBroadcastAllowed: plan.callBroadcast,
		isAgentAccessAllowed: plan.agentAccess,
		isAgentIaAllowed: plan.agentIa
	};
}
